import { useState, useEffect, useCallback } from 'react';

interface PersistentState {
  mapCenter: [number, number];
  mapZoom: number;
  lastSelectedPlaceId: string | null;
}

const STORAGE_KEY = 'droppoint-management-state';

const getDefaultState = (): PersistentState => ({
  mapCenter: [53.3498, -6.2603], // Dublin default
  mapZoom: 15,
  lastSelectedPlaceId: null,
});

export const useStatePersistence = () => {
  const [persistentState, setPersistentState] = useState<PersistentState>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate the structure
        if (
          parsed &&
          Array.isArray(parsed.mapCenter) &&
          parsed.mapCenter.length === 2 &&
          typeof parsed.mapZoom === 'number'
        ) {
          return { ...getDefaultState(), ...parsed };
        }
      }
    } catch (error) {
      console.warn('Failed to load persisted state:', error);
    }
    return getDefaultState();
  });

  // Save state to localStorage
  const saveState = useCallback((updates: Partial<PersistentState>) => {
    setPersistentState(prev => {
      const newState = { ...prev, ...updates };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
      } catch (error) {
        console.warn('Failed to persist state:', error);
      }
      return newState;
    });
  }, []);

  // Update map position
  const updateMapPosition = useCallback((center: [number, number], zoom: number) => {
    saveState({ mapCenter: center, mapZoom: zoom });
  }, [saveState]);

  // Update last selected place
  const updateLastSelectedPlace = useCallback((placeId: string | null) => {
    saveState({ lastSelectedPlaceId: placeId });
  }, [saveState]);

  // Clear persisted data
  const clearPersistedState = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setPersistentState(getDefaultState());
    } catch (error) {
      console.warn('Failed to clear persisted state:', error);
    }
  }, []);

  return {
    persistentState,
    updateMapPosition,
    updateLastSelectedPlace,
    clearPersistedState,
  };
};
